import { SelectionModel } from '@angular/cdk/collections';
import { OverlayRef } from '@angular/cdk/overlay';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { ScrollingModule as ExperimentalScrollingModule } from '@angular/cdk-experimental/scrolling';
import {
  type AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  type ElementRef,
  type OnInit,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { PalantirCheckboxComponent } from '../checkbox/checkbox.component';
import { PalantirContentComponent } from '../content/content.component';
import { PalantirInputComponent } from '../input/input.component';
import { PalantirSkeletonComponent } from '../skeleton/skeleton.component';
import { PalantirTextComponent } from '../text/text.component';
import { PalantirSelectableItemOverlayComponent } from './components/selectable-item-overlay/selectable-item-overlay.component';
import type {
  PalantirSelectItemOverlayItem,
  PalantirSelectItemOverlayOptions,
} from './select-item-overlay.type';

@Component({
  selector: 'palantir-select-item-overlay',
  templateUrl: './select-item-overlay.component.html',
  styleUrls: ['./select-item-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    PalantirTextComponent,
    PalantirInputComponent,
    PalantirCheckboxComponent,
    PalantirSkeletonComponent,
    PalantirSelectableItemOverlayComponent,
    ReactiveFormsModule,
    FormsModule,
    ScrollingModule,
    ExperimentalScrollingModule,
    PalantirContentComponent,
  ],
})
export class PalantirSelectItemOverlayComponent implements OnInit, AfterViewInit {
  @ViewChild('content') private content: ElementRef<HTMLDivElement> | null = null;

  public items = signal<PalantirSelectItemOverlayItem[]>([]);

  public allOriginalItems = signal<any[]>([]);

  public allItems = signal<PalantirSelectItemOverlayItem[]>([]);

  public searchControl = new FormControl('');

  public isScrolling = signal(false);

  public limitItems = signal<number>(0);

  public model = new SelectionModel<number | string>(false, []);

  private isLoadingMore = signal<boolean>(false);

  private skip = signal<number>(0);

  public isLoading = signal<boolean>(false);

  public overlayRef!: OverlayRef;

  public data!: PalantirSelectItemOverlayOptions<any>;

  private destroyRef = inject(DestroyRef);

  public initializeData() {
    this.allOriginalItems.set(this.data.items);
    this.items.set(this.data.items.map((item: any) => this.data.map(item)));
    this.allItems.set(this.getFlatItems(this.data.items));
    this.limitItems.set(this.data.limitItems ?? 0);

    this.model = new SelectionModel<number | string>(this.data.type === 'multiple', []);
  }

  public ngOnInit() {
    if (!this.data) return;

    this.searchControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(async (value: string | null) => {
        if (this.data.infiniteScroll?.enabled) {
          this.skip.set(0);
        }

        let items = await this.data.search(value);
        items = items.map((item: any) => this.data.map(item));

        this.allOriginalItems.update((originalItems) => {
          const newUniqueItems = items.filter(
            (newItem) =>
              !originalItems.some(
                (existingItem) =>
                  existingItem[this.data.itemKey ?? 'id'] === newItem[this.data.itemKey ?? 'id'],
              ),
          );

          return originalItems.concat(newUniqueItems);
        });

        this.items.set(items);
      });

    const allSelectedItems = this.getAllSelectedItems();
    this.model.select(...allSelectedItems.map((item) => item.id));
  }

  public ngAfterViewInit(): void {
    this.setupScrollListener();
  }

  public isAllSelected() {
    return this.getFlatItems(this.items()).every((item: any) =>
      this.model.selected.includes(item[this.data.itemKey ?? 'id']),
    );
  }

  public toggleAll() {
    const flatItems = this.getFlatItems(this.items());
    const items = flatItems.filter((item: any) => !item.disabled);

    if (this.isAllSelected()) {
      this.model.deselect(...items.map((item: any) => item[this.data.itemKey ?? 'id']));
      return;
    }

    if (this.limitItems() && this.model.selected.length >= this.limitItems()) {
      this.model.deselect(...items.map((item: any) => item[this.data.itemKey ?? 'id']));
      return;
    }

    if (this.limitItems()) {
      this.model.select(
        ...items.slice(0, this.limitItems()).map((item: any) => item[this.data.itemKey ?? 'id']),
      );
      return;
    }

    this.model.select(...items.map((item: any) => item[this.data.itemKey ?? 'id']));
  }

  public getOriginalItems(): any[] {
    return this.filterItemsRecursively(this.allOriginalItems());
  }

  public filterItemsRecursively(items: any[]): any[] {
    return items.filter((item: any) => {
      if (item.children?.length) {
        item.children = this.filterItemsRecursively(item.children);
      }

      return this.model.selected.includes(item[this.data.itemKey ?? 'id']);
    });
  }

  private getFlatItems(items: any[]): any[] {
    const flatItems: any[] = [];
    for (const item of items) {
      flatItems.push(item);

      if (item.children) {
        flatItems.push(...this.getFlatItems(item.children));
      }
    }

    return flatItems;
  }

  private getAllSelectedItems(): any[] {
    const items = this.getFlatItems(this.items());
    return items.filter((item) => item.checked);
  }

  public trackByFn(_index: number, item: PalantirSelectItemOverlayItem): number | string {
    return item.id;
  }

  private setupScrollListener(): void {
    if (!this.content?.nativeElement) return;

    const scrollContainer = this.content.nativeElement;

    scrollContainer.onscroll = async () => {
      const scrollTop = scrollContainer.scrollTop;
      const scrollHeight = scrollContainer.scrollHeight;
      const clientHeight = scrollContainer.clientHeight;

      this.isScrolling.set(scrollTop >= 60);

      if (this.data.infiniteScroll?.enabled && scrollTop + clientHeight >= scrollHeight - 100) {
        await this.loadMoreItems();
      }
    };
  }

  private async loadMoreItems() {
    if (this.isLoadingMore() || !this.data.infiniteScroll?.enabled) return;

    this.isLoadingMore.set(true);
    this.isLoading.set(true);

    const take = this.data.infiniteScroll.take ?? 20;
    const currentCount = this.items().length;

    const newItems = await this.data.infiniteScroll!.loadMore(
      currentCount,
      take,
      this.searchControl.value,
    );
    const mappedItems = newItems.map((item) => this.data.map(item));

    this.allOriginalItems.update((orig) => [...orig, ...newItems]);
    this.items.update((curr) => [...curr, ...mappedItems]);
    this.allItems.update((curr) => [...curr, ...mappedItems]);

    this.isLoadingMore.set(false);
    this.isLoading.set(false);
  }
}
